import { useEffect } from 'react'
import { authService } from '@/services/auth.service'
import { useAuthStore } from '@/stores/auth-store'
import { supabase } from '@/lib/supabase'
import { Sentry } from '@/lib/sentry'

/**
 * Initialize auth state on app load and listen to auth changes
 */
export function useAuthInit() {
  const { auth } = useAuthStore()

  useEffect(() => {
    // Get auth methods from store to avoid stale closures
    const authStore = useAuthStore.getState().auth

    // Check for existing session on mount
    const initializeAuth = async () => {
      try {
        const session = await authService.getSession()

        if (session?.user) {
          authStore.setSession(session)
          authStore.setUser({
            id: session.user.id,
            email: session.user.email!,
            role: ['user'],
            session,
          })
          Sentry.setUser({ id: session.user.id, email: session.user.email })
        } else {
          authStore.reset()
        }
      } catch (error) {
        console.error('Auth initialization error:', error)
        authStore.reset()
      }
    }

    initializeAuth()

    // Listen to auth state changes
    const { data: authListener } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        console.log('Auth state changed:', event)
        const authStore = useAuthStore.getState().auth

        if (event === 'SIGNED_IN' && session?.user) {
          authStore.setSession(session)
          authStore.setUser({
            id: session.user.id,
            email: session.user.email!,
            role: ['user'],
            session,
          })
          Sentry.setUser({ id: session.user.id, email: session.user.email })
        }

        if (event === 'SIGNED_OUT') {
          authStore.reset()
          Sentry.setUser(null)
        }

        if (event === 'TOKEN_REFRESHED' && session) {
          authStore.setSession(session)
        }

        if (event === 'PASSWORD_RECOVERY' && session?.user) {
          authStore.setSession(session)
          authStore.setUser({
            id: session.user.id,
            email: session.user.email!,
            role: ['user'],
            session,
          })
          window.location.href = '/reset-password'
        }
      }
    )

    // Cleanup subscription on unmount
    return () => {
      authListener.subscription.unsubscribe()
    }
  }, []) // Empty deps - run once on mount

  return { isLoading: auth.isLoading }
}
